'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { FeatherAlertOctagon, FeatherAlertTriangle, FeatherArrowRight, FeatherCheckCircle, FeatherChevronDown } from '@subframe/core';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
export type PreValidationSeverity = 'CRITICAL' | 'WARNING';
export interface AggregatedIssue {
  tipo: string;
  severity: PreValidationSeverity;
  coluna: string;
  quantidade: number;
  linhas: number[];
  linhasTruncadas: boolean;
  mensagem: string;
  comoCorrigir: string;
  linhasPreview?: Array<{
    linha: number;
    valor: string;
  }>;
}
export interface SpreadsheetValidationReportProps {
  issues: AggregatedIssue[];
  hasCritical: boolean;
  totalRows?: number;
  fileName?: string;
  onDismiss?: () => void;
  onRetry?: () => void;
  onProceedWithoutFixing?: () => void;
  className?: string;
}
type KnownIssueType = 'CPF_SEM_ZERO_ESQUERDA' | 'CNPJ_SEM_ZERO_ESQUERDA' | 'CPF_CNPJ_DUPLICADO' | 'CPF_CNPJ_INVALIDO' | 'VALOR_INVALIDO' | 'PLANILHA_SEM_DADOS' | 'TELEFONE_COMO_MOEDA' | 'ENCODING_QUEBRADO' | 'CAMPO_OBRIGATORIO_VAZIO' | 'CAMPO_OPCIONAL_VAZIO';
const ISSUE_TYPE_LABEL: Record<KnownIssueType, string> = {
  CPF_SEM_ZERO_ESQUERDA: 'CPF com zero à esquerda removido',
  CNPJ_SEM_ZERO_ESQUERDA: 'CNPJ com zero à esquerda removido',
  CPF_CNPJ_DUPLICADO: 'CPF/CNPJ duplicado',
  CPF_CNPJ_INVALIDO: 'CPF/CNPJ inválido',
  VALOR_INVALIDO: 'Valor inválido',
  PLANILHA_SEM_DADOS: 'Planilha sem dados',
  TELEFONE_COMO_MOEDA: 'Telefone formatado como moeda',
  ENCODING_QUEBRADO: 'Caracteres corrompidos',
  CAMPO_OBRIGATORIO_VAZIO: 'Campo obrigatório vazio',
  CAMPO_OPCIONAL_VAZIO: 'Campo opcional vazio'
};
function formatTypeLabel(tipo: string): string {
  return ISSUE_TYPE_LABEL[tipo as KnownIssueType] ?? tipo.toLowerCase().replace(/_/g, ' ');
}
function parseSteps(comoCorrigir: string): string[] {
  const sentences = comoCorrigir.split(/(?<=[.:])\s+|\s*→\s*/g).map(s => s.trim()).filter(s => s.length > 0 && s !== '.');
  return sentences.length > 1 ? sentences : [comoCorrigir];
}
const SOFT_EASE = [0.22, 1, 0.36, 1] as const;
interface IssueRowProps {
  issue: AggregatedIssue;
  expanded: boolean;
  onToggle: () => void;
  isLast: boolean;
}
function IssueRow(t0) {
  const $ = _c(66);
  const {
    issue,
    expanded,
    onToggle,
    isLast
  } = t0;
  const prefersReducedMotion = useReducedMotion();
  const panelId = useId();
  const isCritical = issue.severity === "CRITICAL";
  let t1;
  if ($[0] !== issue.comoCorrigir) {
    t1 = parseSteps(issue.comoCorrigir);
    $[0] = issue.comoCorrigir;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const steps = t1;
  const dotClass = isCritical ? "bg-error-500" : "bg-warning-500";
  const badgeClass = isCritical ? "bg-error-50 text-error-700 border-error-200" : "bg-warning-50 text-warning-700 border-warning-200";
  const severityLabel = isCritical ? "Cr\xEDtico" : "Aviso";
  let T0;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[2] !== badgeClass || $[3] !== dotClass || $[4] !== expanded || $[5] !== isLast || $[6] !== issue.coluna || $[7] !== issue.comoCorrigir || $[8] !== issue.linhas || $[9] !== issue.linhasPreview || $[10] !== issue.linhasTruncadas || $[11] !== issue.quantidade || $[12] !== issue.tipo || $[13] !== onToggle || $[14] !== panelId || $[15] !== prefersReducedMotion || $[16] !== severityLabel || $[17] !== steps) {
    const rows = issue.linhasPreview?.map(_temp) ?? issue.linhas.map(_temp2);
    t4 = isLast ? "" : "border-b border-neutral-100";
    const t6 = `inline-block h-2 w-2 flex-shrink-0 rounded-full ${dotClass}`;
    let t7;
    if ($[23] !== t6) {
      t7 = <span aria-hidden={true} className={t6} />;
      $[23] = t6;
      $[24] = t7;
    } else {
      t7 = $[24];
    }
    const t8 = `inline-flex h-5 items-center rounded-md border px-1.5 text-[11px] font-[600] tracking-tight ${badgeClass}`;
    let t9;
    if ($[25] !== severityLabel || $[26] !== t8) {
      t9 = <span className={t8}>{severityLabel}</span>;
      $[25] = severityLabel;
      $[26] = t8;
      $[27] = t9;
    } else {
      t9 = $[27];
    }
    let t10;
    if ($[28] !== issue.tipo) {
      t10 = formatTypeLabel(issue.tipo);
      $[28] = issue.tipo;
      $[29] = t10;
    } else {
      t10 = $[29];
    }
    let t11;
    if ($[30] !== t10) {
      t11 = <span className="text-body font-body text-default-font min-w-0 truncate">{t10}</span>;
      $[30] = t10;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    let t12;
    if ($[32] !== issue.quantidade) {
      t12 = <span className="text-default-font font-[600] tabular-nums">{issue.quantidade}</span>;
      $[32] = issue.quantidade;
      $[33] = t12;
    } else {
      t12 = $[33];
    }
    const t13 = issue.quantidade === 1 ? "linha" : "linhas";
    let t14;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <span aria-hidden={true} className="text-neutral-300">·</span>;
      $[34] = t14;
    } else {
      t14 = $[34];
    }
    let t15;
    if ($[35] !== issue.coluna) {
      t15 = <span className="text-default-font rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-[11px]">{issue.coluna}</span>;
      $[35] = issue.coluna;
      $[36] = t15;
    } else {
      t15 = $[36];
    }
    let t16;
    if ($[37] !== t12 || $[38] !== t13 || $[39] !== t15) {
      t16 = <span className="text-caption font-caption text-subtext-color inline-flex items-center gap-1.5">{t12}{t13}{t14}{t15}</span>;
      $[37] = t12;
      $[38] = t13;
      $[39] = t15;
      $[40] = t16;
    } else {
      t16 = $[40];
    }
    let t17;
    if ($[41] !== t11 || $[42] !== t16 || $[43] !== t9) {
      t17 = <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">{t9}{t11}{t16}</div>;
      $[41] = t11;
      $[42] = t16;
      $[43] = t9;
      $[44] = t17;
    } else {
      t17 = $[44];
    }
    const t18 = expanded ? 180 : 0;
    let t19;
    if ($[45] !== t18) {
      t19 = {
        rotate: t18
      };
      $[45] = t18;
      $[46] = t19;
    } else {
      t19 = $[46];
    }
    let t20;
    if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = {
        duration: 0.25,
        ease: SOFT_EASE
      };
      $[47] = t20;
    } else {
      t20 = $[47];
    }
    let t21;
    if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <FeatherChevronDown className="h-4 w-4" />;
      $[48] = t21;
    } else {
      t21 = $[48];
    }
    let t22;
    if ($[49] !== t19) {
      t22 = <motion.span aria-hidden={true} animate={t19} transition={t20} className="text-subtext-color group-hover:text-default-font ml-auto inline-flex h-6 w-6 flex-shrink-0 items-center justify-center">{t21}</motion.span>;
      $[49] = t19;
      $[50] = t22;
    } else {
      t22 = $[50];
    }
    if ($[51] !== expanded || $[52] !== onToggle || $[53] !== panelId || $[54] !== t17 || $[55] !== t22 || $[56] !== t7) {
      t5 = <button type="button" onClick={onToggle} aria-expanded={expanded} aria-controls={panelId} className="focus-visible:ring-brand-500 group flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-neutral-50 focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset sm:px-5">{t7}{t17}{t22}</button>;
      $[51] = expanded;
      $[52] = onToggle;
      $[53] = panelId;
      $[54] = t17;
      $[55] = t22;
      $[56] = t7;
      $[57] = t5;
    } else {
      t5 = $[57];
    }
    T0 = AnimatePresence;
    t2 = false;
    t3 = expanded ? <motion.div key="panel" id={panelId} role="region" aria-label={`Detalhes de ${formatTypeLabel(issue.tipo)}`} initial={prefersReducedMotion ? {
      opacity: 0
    } : {
      height: 0,
      opacity: 0
    }} animate={prefersReducedMotion ? {
      opacity: 1
    } : {
      height: "auto",
      opacity: 1
    }} exit={prefersReducedMotion ? {
      opacity: 0
    } : {
      height: 0,
      opacity: 0
    }} transition={{
      duration: 0.28,
      ease: SOFT_EASE
    }} className="overflow-hidden"><div className="grid grid-cols-1 gap-4 bg-neutral-50 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] sm:gap-6 sm:px-5 sm:py-4"><div><h5 className="text-caption-bold font-caption-bold text-subtext-color tracking-[0.06em] uppercase">Linhas afetadas</h5><ul className="mt-2 flex flex-col">{rows.map(_temp3)}</ul>{issue.linhasTruncadas ? <p className="text-caption font-caption text-subtext-color mt-2">…e mais {issue.quantidade - issue.linhas.length}{" "}{issue.quantidade - issue.linhas.length === 1 ? "linha" : "linhas"}</p> : null}</div><div><h5 className="text-caption-bold font-caption-bold text-subtext-color tracking-[0.06em] uppercase">Como corrigir</h5>{steps.length > 1 ? <ol className="mt-2 flex flex-col gap-1.5">{steps.map(_temp4)}</ol> : <p className="text-body font-body text-default-font mt-2 leading-snug">{issue.comoCorrigir}</p>}</div></div></motion.div> : null;
    $[2] = badgeClass;
    $[3] = dotClass;
    $[4] = expanded;
    $[5] = isLast;
    $[6] = issue.coluna;
    $[7] = issue.comoCorrigir;
    $[8] = issue.linhas;
    $[9] = issue.linhasPreview;
    $[10] = issue.linhasTruncadas;
    $[11] = issue.quantidade;
    $[12] = issue.tipo;
    $[13] = onToggle;
    $[14] = panelId;
    $[15] = prefersReducedMotion;
    $[16] = severityLabel;
    $[17] = steps;
    $[18] = T0;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
  } else {
    T0 = $[18];
    t2 = $[19];
    t3 = $[20];
    t4 = $[21];
    t5 = $[22];
  }
  let t6;
  if ($[58] !== T0 || $[59] !== t2 || $[60] !== t3) {
    t6 = <T0 initial={t2}>{t3}</T0>;
    $[58] = T0;
    $[59] = t2;
    $[60] = t3;
    $[61] = t6;
  } else {
    t6 = $[61];
  }
  let t7;
  if ($[62] !== t4 || $[63] !== t5 || $[64] !== t6) {
    t7 = <div className={t4}>{t5}{t6}</div>;
    $[62] = t4;
    $[63] = t5;
    $[64] = t6;
    $[65] = t7;
  } else {
    t7 = $[65];
  }
  return t7;
}
function _temp4(step, idx) {
  return <li key={idx} className="text-body font-body text-default-font flex gap-2.5 leading-snug"><span className="text-default-font bg-brand-50 mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full font-mono text-[11px] font-[600]">{idx + 1}</span><span>{step}</span></li>;
}
function _temp3(item) {
  return <li key={item.linha} className="flex items-baseline gap-3 py-1"><span className="text-caption text-subtext-color inline-flex min-w-[2.5rem] justify-end font-mono font-[600] tabular-nums">#{item.linha}</span>{item.valor === null ? <span className="text-caption font-caption text-subtext-color italic">(valor indisponível)</span> : item.valor === "" ? <span className="text-caption font-caption text-subtext-color italic">(célula vazia)</span> : <span className="text-caption text-default-font min-w-0 truncate font-mono" title={item.valor}>{item.valor}</span>}</li>;
}
function _temp2(linha) {
  return {
    linha,
    valor: null as string | null
  };
}
function _temp(p) {
  return {
    linha: p.linha,
    valor: p.valor as string | null
  };
}
export const SpreadsheetValidationReport = t0 => {
  const $ = _c(68);
  const {
    issues,
    hasCritical,
    totalRows,
    onRetry,
    onProceedWithoutFixing,
    className
  } = t0;
  const prefersReducedMotion = useReducedMotion();
  const sectionRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== prefersReducedMotion) {
    t1 = () => {
      sectionRef.current?.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "start"
      });
    };
    t2 = [prefersReducedMotion];
    $[0] = prefersReducedMotion;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let affected;
  let criticalRows;
  let t3;
  let warningRows;
  if ($[3] !== issues) {
    const crit = issues.filter(_temp5);
    const warn = issues.filter(_temp6);
    criticalRows = new Set();
    warningRows = new Set();
    for (const issue of crit) {
      issue.linhas.forEach(l => criticalRows.add(l));
    }
    for (const issue_0 of warn) {
      issue_0.linhas.forEach(l_0 => warningRows.add(l_0));
    }
    affected = new Set([...criticalRows, ...warningRows]);
    t3 = [...crit, ...warn];
    $[3] = issues;
    $[4] = affected;
    $[5] = criticalRows;
    $[6] = t3;
    $[7] = warningRows;
  } else {
    affected = $[4];
    criticalRows = $[5];
    t3 = $[6];
    warningRows = $[7];
  }
  let t4;
  if ($[8] !== affected.size || $[9] !== criticalRows.size || $[10] !== t3 || $[11] !== warningRows.size) {
    t4 = {
      orderedIssues: t3,
      affectedRowCount: affected.size,
      criticalRowCount: criticalRows.size,
      warningRowCount: warningRows.size
    };
    $[8] = affected.size;
    $[9] = criticalRows.size;
    $[10] = t3;
    $[11] = warningRows.size;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const {
    orderedIssues,
    affectedRowCount,
    criticalRowCount,
    warningRowCount
  } = t4;
  const [expandedKeys, setExpandedKeys] = useState(_temp7);
  if (issues.length === 0) {
    return null;
  }
  const resolvedTotal = totalRows ?? affectedRowCount;
  const okRows = Math.max(resolvedTotal - criticalRowCount - warningRowCount, 0);
  const okPct = resolvedTotal > 0 ? okRows / resolvedTotal * 100 : 0;
  const warnPct = resolvedTotal > 0 ? warningRowCount / resolvedTotal * 100 : 0;
  const critPct = resolvedTotal > 0 ? criticalRowCount / resolvedTotal * 100 : 0;
  const toneIconClass = hasCritical ? "bg-error-100 text-error-700" : "bg-warning-100 text-warning-700";
  const canProceedWithoutFixing = hasCritical && Boolean(onProceedWithoutFixing);
  const heroTitle = hasCritical ? canProceedWithoutFixing ? "Encontramos erros que travam o envio" : "Planilha bloqueada para envio" : "Alguns avisos na planilha";
  const heroSubtitle = hasCritical ? canProceedWithoutFixing ? `${criticalRowCount} ${criticalRowCount === 1 ? "linha precisa" : "linhas precisam"} de correção antes de continuar.` : "Corrija os itens abaixo antes de prosseguir." : "Os avisos n\xE3o impedem o envio \u2014 revise e prossiga.";
  const HeroIcon = hasCritical ? FeatherAlertOctagon : FeatherAlertTriangle;
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = key => setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const toggle = t5;
  let t6;
  if ($[14] !== prefersReducedMotion) {
    t6 = prefersReducedMotion ? false : {
      opacity: 0,
      y: 6
    };
    $[14] = prefersReducedMotion;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      opacity: 1,
      y: 0
    };
    t8 = {
      duration: 0.32,
      ease: SOFT_EASE
    };
    $[16] = t7;
    $[17] = t8;
  } else {
    t7 = $[16];
    t8 = $[17];
  }
  const t9 = `bg-default-background flex w-full flex-col rounded-[16px] border border-solid border-neutral-200 shadow-sm ${className ?? ""}`;
  const t10 = `mt-0.5 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${toneIconClass}`;
  let t11;
  if ($[18] !== HeroIcon) {
    t11 = <HeroIcon className="h-4 w-4" />;
    $[18] = HeroIcon;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t10 || $[21] !== t11) {
    t12 = <div aria-hidden={true} className={t10}>{t11}</div>;
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== heroTitle) {
    t13 = <h3 className="text-body-bold font-body-bold text-default-font leading-tight">{heroTitle}</h3>;
    $[23] = heroTitle;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== affectedRowCount || $[26] !== critPct || $[27] !== criticalRowCount || $[28] !== okPct || $[29] !== okRows || $[30] !== prefersReducedMotion || $[31] !== resolvedTotal || $[32] !== warnPct || $[33] !== warningRowCount) {
    t14 = false && resolvedTotal > 0 ? <div className="flex flex-col gap-2"><div role="progressbar" aria-label={`${affectedRowCount} de ${resolvedTotal} linhas precisam de atenção`} aria-valuenow={affectedRowCount} aria-valuemin={0} aria-valuemax={resolvedTotal} className="flex h-1.5 w-full overflow-hidden rounded-full bg-neutral-100"><motion.span className="bg-success-500 block h-full" initial={prefersReducedMotion ? false : {
          width: 0
        }} animate={{
          width: `${okPct}%`
        }} transition={{
          duration: 0.6,
          ease: SOFT_EASE,
          delay: 0.1
        }} /><motion.span className="bg-warning-500 block h-full" initial={prefersReducedMotion ? false : {
          width: 0
        }} animate={{
          width: `${warnPct}%`
        }} transition={{
          duration: 0.6,
          ease: SOFT_EASE,
          delay: 0.22
        }} /><motion.span className="bg-error-500 block h-full" initial={prefersReducedMotion ? false : {
          width: 0
        }} animate={{
          width: `${critPct}%`
        }} transition={{
          duration: 0.6,
          ease: SOFT_EASE,
          delay: 0.34
        }} /></div><div className="text-caption font-caption text-subtext-color flex flex-wrap items-center gap-x-4 gap-y-1">{okRows > 0 ? <span className="inline-flex items-center gap-1.5"><FeatherCheckCircle className="text-success-600 h-3 w-3" aria-hidden={true} /><span className="text-default-font font-[600] tabular-nums">{okRows}</span> OK</span> : null}{warningRowCount > 0 ? <span className="inline-flex items-center gap-1.5"><FeatherAlertTriangle className="text-warning-600 h-3 w-3" aria-hidden={true} /><span className="text-default-font font-[600] tabular-nums">{warningRowCount}</span>{" "}{warningRowCount === 1 ? "aviso" : "avisos"}</span> : null}{criticalRowCount > 0 ? <span className="inline-flex items-center gap-1.5"><FeatherAlertOctagon className="text-error-600 h-3 w-3" aria-hidden={true} /><span className="text-default-font font-[600] tabular-nums">{criticalRowCount}</span>{" "}{criticalRowCount === 1 ? "cr\xEDtico" : "cr\xEDticos"}</span> : null}<span className="text-subtext-color tabular-nums">de {resolvedTotal} {resolvedTotal === 1 ? "linha" : "linhas"}</span></div></div> : null;
    $[25] = affectedRowCount;
    $[26] = critPct;
    $[27] = criticalRowCount;
    $[28] = okPct;
    $[29] = okRows;
    $[30] = prefersReducedMotion;
    $[31] = resolvedTotal;
    $[32] = warnPct;
    $[33] = warningRowCount;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const t15 = `text-caption font-caption leading-snug ${hasCritical ? "text-error-700" : "text-subtext-color"}`;
  let t16;
  if ($[35] !== heroSubtitle || $[36] !== t15) {
    t16 = <p role="status" aria-live="polite" className={t15}>{heroSubtitle}</p>;
    $[35] = heroSubtitle;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t13 || $[39] !== t14 || $[40] !== t16) {
    t17 = <div className="flex min-w-0 flex-1 flex-col gap-2">{t13}{t14}{t16}</div>;
    $[38] = t13;
    $[39] = t14;
    $[40] = t16;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== t12 || $[43] !== t17) {
    t18 = <div className="flex items-start gap-3 p-4 sm:gap-4 sm:p-5">{t12}{t17}</div>;
    $[42] = t12;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== issues.length) {
    t19 = <div className="px-4 pt-3 pb-1.5 sm:px-5"><h4 className="text-caption-bold font-caption-bold text-subtext-color tracking-[0.06em] uppercase">Tipos de problema<span className="text-subtext-color ml-2 normal-case tabular-nums">({issues.length})</span></h4></div>;
    $[45] = issues.length;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== expandedKeys || $[48] !== orderedIssues) {
    let t21;
    if ($[50] !== expandedKeys || $[51] !== orderedIssues.length) {
      t21 = (issue_1, idx) => {
        const key_0 = `${issue_1.tipo}-${issue_1.coluna}`;
        return <IssueRow key={key_0} issue={issue_1} expanded={expandedKeys.has(key_0)} onToggle={() => toggle(key_0)} isLast={idx === orderedIssues.length - 1} />;
      };
      $[50] = expandedKeys;
      $[51] = orderedIssues.length;
      $[52] = t21;
    } else {
      t21 = $[52];
    }
    t20 = orderedIssues.map(t21);
    $[47] = expandedKeys;
    $[48] = orderedIssues;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[53] !== t20) {
    t21 = <div>{t20}</div>;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== t19 || $[56] !== t21) {
    t22 = <div className="flex flex-col border-t border-neutral-200">{t19}{t21}</div>;
    $[55] = t19;
    $[56] = t21;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== canProceedWithoutFixing || $[59] !== onProceedWithoutFixing || $[60] !== onRetry) {
    t23 = onRetry || canProceedWithoutFixing ? <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-neutral-200 bg-neutral-50 px-4 py-3 sm:px-5">{canProceedWithoutFixing ? <button type="button" onClick={onProceedWithoutFixing} className="text-caption-bold font-caption-bold text-default-font focus-visible:ring-brand-500 inline-flex h-9 items-center justify-center rounded-[8px] px-4 transition-colors hover:bg-neutral-100 focus-visible:ring-2 focus-visible:outline-none">Seguir sem corrigir</button> : null}{onRetry ? <button type="button" onClick={onRetry} className="bg-brand-700 hover:bg-brand-600 text-caption-bold font-caption-bold text-neutral-0 focus-visible:ring-brand-500 group inline-flex h-9 items-center justify-center gap-2 rounded-[8px] px-4 transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none">Corrigir e enviar nova planilha<FeatherArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden={true} /></button> : null}</footer> : null;
    $[58] = canProceedWithoutFixing;
    $[59] = onProceedWithoutFixing;
    $[60] = onRetry;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== t18 || $[63] !== t22 || $[64] !== t23 || $[65] !== t6 || $[66] !== t9) {
    t24 = <motion.section ref={sectionRef} role="region" aria-label={"Relat\xF3rio de valida\xE7\xE3o da planilha"} initial={t6} animate={t7} transition={t8} className={t9}>{t18}{t22}{t23}</motion.section>;
    $[62] = t18;
    $[63] = t22;
    $[64] = t23;
    $[65] = t6;
    $[66] = t9;
    $[67] = t24;
  } else {
    t24 = $[67];
  }
  return t24;
};
function _temp5(i) {
  return i.severity === "CRITICAL";
}
function _temp6(i_0) {
  return i_0.severity === "WARNING";
}
function _temp7() {
  return new Set();
}